import { isBlank } from '../persistence.utils';

/**
 * The description of a calendar entry (calendar quick wins B, tasks 3.4 and 5) - one function for the client
 * download, the Google / Outlook links and the attachment of the vote confirmation mail, so the app and the mail
 * cannot drift apart. The caller picks the labels: which line 1 (owner or participant) and which line 2 / which
 * link (organizer, response or plain url, workorder 1.12) stays with the caller; this function only joins and
 * fills them.
 *
 * - the poll description first, when there is one, verbatim (no placeholder in it is touched)
 * - then line 1 and line 2, each filled with {BRAND_NAME}, {POLL_TITLE}, {POLL_OWNER}, {POLL_URL}, {NEW_LINE}
 *   in one pass - a value is never scanned as a placeholder again
 * - a blank line between the parts, none at the end
 * - without an owner name (anonymous organizer) line1NoOwner replaces line 1 - never "undefined's event"
 */
export interface CalendarDescriptionLabels {
    /** iCal.ownerLine1 or iCal.notOwnerLine1 */
    line1: string;
    /** iCal.notOwnerLine1NoOwner - taken instead of line1 when the owner name is blank; leave out where line1 names no owner */
    line1NoOwner?: string;
    /** iCal.ownerLine2, ownerLine2Anonymous, notOwnerLine2 or notOwnerLine2Anonymous */
    line2: string;
}

export interface CalendarDescriptionValues {
    pollDescription?: string | null;
    pollTitle?: string | null;
    ownerName?: string | null;
    /** the link of line 2 */
    pollUrl: string;
    /** "Whocan" */
    brandName: string;
}

export function buildCalendarDescription(labels: CalendarDescriptionLabels, values: CalendarDescriptionValues): string {
    const ownerName = isBlank(values.ownerName) ? '' : String(values.ownerName).trim();
    const line1 = !ownerName && labels.line1NoOwner ? labels.line1NoOwner : labels.line1;
    const replacements: Record<string, string> = {
        BRAND_NAME: values.brandName ?? '',
        POLL_TITLE: isBlank(values.pollTitle) ? '' : String(values.pollTitle).trim(),
        POLL_OWNER: ownerName,
        POLL_URL: values.pollUrl ?? '',
        NEW_LINE: '\n',
    };
    const fill = (label: string) => (label ?? '').replace(/{(BRAND_NAME|POLL_TITLE|POLL_OWNER|POLL_URL|NEW_LINE)}/g, (_match, key) => replacements[key]);
    const parts: string[] = [];
    if (!isBlank(values.pollDescription)) parts.push(String(values.pollDescription).trim());
    parts.push(fill(line1), fill(labels.line2));
    return parts.filter(part => part !== '').join('\n\n');
}

/**
 * The description of one event with its meeting link in front (calendar quick wins C): "Online: https://…", a blank
 * line, then the description - for the .ics, the Google and the Outlook link alike. First, so the 1500 characters of
 * the calendar links never cut the link. Without a link the description stays as it is.
 * @param onlineLabel vote.online of the poll template ("Online:")
 */
export function withMeetingLinkLine(description: string, meetingLink: string | undefined, onlineLabel: string): string {
    if (!meetingLink) return description;
    const line = `${onlineLabel ?? ''} ${meetingLink}`.trim();
    return [line, description].filter(part => !!part).join('\n\n');
}
